import { z } from "zod";
const Text = z.string().min(1);
const Amount = z.number().finite();
export const OpexReviewMappingsSchema = z
  .object({
    costCenters: z
      .array(z.object({ sourceFile: Text, targetCostCenterCode: Text, evidence: Text }).strict())
      .default([]),
    suppliers: z
      .array(z.object({ sourceRowSha256: Text, supplierId: Text, evidence: Text }).strict())
      .default([]),
    costCenterSplits: z
      .array(
        z
          .object({
            sourceFile: Text,
            allocations: z
              .array(
                z
                  .object({
                    targetCostCenterCode: Text,
                    weightBps: z.number().int().positive().max(10000),
                  })
                  .strict(),
              )
              .min(2),
            evidence: Text,
          })
          .strict(),
      )
      .optional(),
  })
  .strict();
export type OpexReviewMappings = z.infer<typeof OpexReviewMappingsSchema>;
const OpexMappingReviewSchema = z.object({
  departments: z.array(z.object({ sourceFile: Text, sourceScope: Text, total: Amount })),
  vendors: z.array(
    z.object({
      sourceFile: Text,
      sourceRow: z.number().int().positive(),
      sourceRowSha256: Text,
      sourceLabel: Text,
      supplierId: Text.nullable(),
      total: Amount,
    }),
  ),
  suppliers: z.array(z.object({ supplierId: Text, displayName: Text })),
  costCenters: z.array(z.object({ code: Text, displayName: Text })),
});
export type OpexMappingReview = z.infer<typeof OpexMappingReviewSchema>;
export const OpexImportDataSchema = z.object({
  revision: Text,
  pending_department_count: z.number().int().nonnegative(),
  pending_vendor_rows: z.number().int().nonnegative(),
  suppliers: z.array(
    z.object({
      supplier_id: Text,
      display_name: Text,
      status: Text,
      company_restrictions: z.array(Text),
    }),
  ),
  vendors: z.array(
    z.object({
      cost_center: Text,
      account_code: Text,
      supplier_id: Text,
      source_row_sha256: Text,
      periods: z.array(Text).length(12),
      monthly_amounts: z.array(Amount).length(12),
    }),
  ),
});
export type OpexImportData = z.infer<typeof OpexImportDataSchema>;
export const OpexImportReviewSchema = z.object({
  outcome: z.enum(["PREVIEW", "APPLIED", "ALREADY_APPLIED"]),
  review: OpexMappingReviewSchema.optional(),
  summary: z.object({
    revision: Text,
    source_version: Text,
    source_total: Amount,
    mapped_total: Amount,
    opex_default_total: Amount,
    suppliers: z.number().int(),
    mapped_vendor_rows: z.number().int(),
    held_vendor_rows: z.number().int().nonnegative().optional(),
    pending_departments: z.array(Text),
    pending_vendor_rows: z.number().int(),
    basis_start_period: Text,
    basis_end_period: Text,
  }),
});
